import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Shield, Mail, Lock, ArrowLeft } from "lucide-react";
import LoginAuth from "@/assets/images/auth/login-auth.jpg";
import { useAdminAuth } from "@/context/Admin";
import { toast } from "sonner";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("password"); // "password" or "otp"
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, verifyOTP, resendOTP, loading, admin, fetchAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      // If admin is already in state, redirect immediately
      if (admin) {
        navigate("/admin");
        return;
      }

      // Check if there's a valid session by fetching admin
      if (!loading) {
        try {
          await fetchAdmin();
        } catch (error) {
          // Admin is not authenticated, stay on login page
        }
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Redirect when admin state becomes available
  useEffect(() => {
    if (admin && !loading) {
      navigate("/admin");
    }
  }, [admin, navigate, loading]);

  // Timer effect for OTP resend
  useEffect(() => {
    if (step !== "otp") {
      return;
    }

    // Reset timer when entering OTP step
    setTimeLeft(600);
    setCanResend(false);

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const result = await login(email, password, rememberMe);
      
      if (result?.requiresOTP === true) {
        toast.success(result.message || "Password verified. Please check your email for OTP verification.");
        setStep("otp");
        setIsLoggingIn(false);
        return;
      }
      
      if (result?.requiresOTP === false) {
        toast.success("Login successful!");
        navigate("/admin");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Login failed. Please try again."
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Handle paste: if value length > 1, it's likely a paste operation
    if (value.length > 1) {
      const digits = value.replace(/\D/g, ""); // Extract only digits
      
      if (digits.length === 0) return;
      
      const newOtp = [...otp];
      
      // If we have 6 digits, fill all fields
      if (digits.length >= 6) {
        for (let i = 0; i < 6; i++) {
          newOtp[i] = digits[i] || "";
        }
        setOtp(newOtp);
        // Focus the last input
        const lastInput = document.getElementById(`admin-otp-5`);
        if (lastInput) lastInput.focus();
        return;
      }
      
      // If less than 6 digits, fill from current index
      for (let i = 0; i < digits.length && (index + i) < 6; i++) {
        newOtp[index + i] = digits[i];
      }
      setOtp(newOtp);
      
      // Focus the next empty input or the last one
      const nextIndex = Math.min(index + digits.length, 5);
      const nextInput = document.getElementById(`admin-otp-${nextIndex}`);
      if (nextInput) nextInput.focus();
      return;
    }

    // Handle single digit input
    const newOtp = [...otp];
    newOtp[index] = value.replace(/\D/g, ""); // Only allow digits
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`admin-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`admin-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e, startIndex = 0) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, ""); // Extract only digits
    
    if (digits.length === 0) return;
    
    const newOtp = [...otp];
    
    // If we have 6 or more digits, always fill from the beginning (index 0)
    // This provides a better UX - pasting "123456" should fill all fields
    if (digits.length >= 6) {
      for (let i = 0; i < 6; i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);
      // Focus the last input
      const lastInput = document.getElementById(`admin-otp-5`);
      if (lastInput) lastInput.focus();
    } else {
      // If less than 6 digits, fill from the start index
      for (let i = 0; i < digits.length && (startIndex + i) < 6; i++) {
        newOtp[startIndex + i] = digits[i];
      }
      setOtp(newOtp);
      // Focus the next empty input or the last one
      const focusIndex = Math.min(startIndex + digits.length, 5);
      const focusInput = document.getElementById(`admin-otp-${focusIndex}`);
      if (focusInput) focusInput.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join("");

    if (code.length !== 6) {
      toast.error("Please enter the 6-digit code.");
      return;
    }

    setIsVerifyingOTP(true);
    try {
      await verifyOTP(email, code);
      toast.success("OTP verified successfully. Login successful!");
      navigate("/admin");
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Invalid or expired OTP."
      );
      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await resendOTP(email);
      setTimeLeft(600);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
    } catch (err) {
      // Error is already handled in the context
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // OTP Verification Step
  if (step === "otp") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Branding */}
          <div className="hidden lg:block">
            <div className="relative">
              <img
                src={LoginAuth}
                alt="OTP verification"
                width={500}
                height={600}
                className="rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-800/50 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <h2 className="text-3xl font-bold mb-2">Verify Your Identity</h2>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-2xl font-bold">Security Check</span>
                </div>
                <p className="text-white/90 max-w-sm">
                  Please check your email for the verification code.
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - OTP Form */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">
                  Enter Verification Code
                </h1>
                <p className="text-gray-600 mb-2">We've sent a 6-digit code to:</p>
                <p className="font-semibold text-primary">{email}</p>
              </div>

              {/* OTP Form */}
              <form onSubmit={handleOtpSubmit} className="space-y-6">
                <div>
                  <Label className="text-gray-800 font-medium mb-4 block text-center">
                    Enter 6-Digit Code
                  </Label>
                  <div className="flex gap-2 justify-center">
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        id={`admin-otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={(e) => handleOtpPaste(e, index)}
                        className="w-12 h-12 text-center text-lg font-semibold border-gray-200 focus:border-primary"
                        required
                      />
                    ))}
                  </div>
                </div>

                {/* Timer and Resend */}
                <div className="text-center space-y-4">
                  {timeLeft > 0 ? (
                    <p className="text-sm text-gray-600">
                      Resend code in{" "}
                      <span className="font-semibold text-primary">
                        {formatTime(timeLeft)}
                      </span>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={loading}
                      className="text-sm text-primary hover:text-primary/80 font-medium disabled:opacity-50"
                    >
                      Resend Code
                    </button>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isVerifyingOTP || loading || otp.join("").length !== 6}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium"
                >
                  {isVerifyingOTP || loading ? "Verifying..." : "Verify Code"}
                </Button>
              </form>

              {/* Back to Login */}
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setStep("password");
                    setOtp(["", "", "", "", "", ""]);
                  }}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password Login Step
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden lg:block">
          <div className="relative">
            <img
              src={LoginAuth}
              alt="login"
              width={500}
              height={600}
              className="rounded-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-800/50 to-transparent rounded-2xl"></div>
            <div className="absolute bottom-8 left-8 text-white">
              <h2 className="text-3xl font-bold mb-2">Admin Access</h2>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-bold">Dashboard</span>
              </div>
              <p className="text-white/90 max-w-sm">
                Securely manage your e-commerce platform.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Admin Login
              </h1>
              <p className="text-gray-600">
                Sign in to your administrator account
              </p>
            </div>

            {/* Social Login */}
            <div className="space-y-3 mb-6">
              <Button
                variant="outline"
                className="w-full border-gray-200 hover:bg-gray-50 py-6 bg-transparent"
                type="button"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </div>

            <div className="relative mb-6">
              <Separator />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="bg-white px-4 text-sm text-gray-500">
                  Or sign in with email
                </span>
              </div>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-gray-800 font-medium">
                  Email Address
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your admin email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-3 border-gray-200 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-800 font-medium">
                  Password
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your admin password"
                    className="pl-10 pr-10 py-3 border-gray-200 focus:border-blue-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember-admin" 
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                  />
                  <Label
                    htmlFor="remember-admin"
                    className="text-sm text-gray-600 cursor-pointer"
                  >
                    Remember me for 30 days
                  </Label>
                </div>
                <Link
                  to="/admin/forgot-password"
                  className="text-sm text-secondary hover:text-secondary/80 font-medium"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={isLoggingIn || loading}
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium"
              >
                {isLoggingIn || loading ? "Signing In..." : "Sign In"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
